 
import { Component, Input, ViewChild, OnInit, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges, Renderer2, AfterViewInit, inject } from "@angular/core";
import {
    NumberTaskCollection,
    NumberTaskCollectionType,
} from "./NumberTaskCollection";
import { PlanungApiOverviewComponent } from "../planung-api-overview.component";
import { Subscription } from "rxjs";
import {
    TaskDto,
    YearPeriod,
    CalendarWeek,
    ResourceType,
    UserAbsenceLevel,
} from "src/app/web-api/web-api";
import { orderBy } from "src/app/helpers/cst-data-query";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import {
    CstDateTimeService,
    DayOfWeek,
} from "src/app/services/cst-date-time.service";
import { addDays } from "date-fns";
import { BFF_IsSaasInstance } from "src/app/injection-tokens/backend-feature-flags";

enum ItemType {
    MonthCW,
    Resource,
    TaskCard,
}

interface IAbsenceItem {
    label: string;
    type: "free" | "partial" | "full";
    tooltip?: string;
}

@Component({
    selector: "app-planung-number-tasks-collection",
    templateUrl: "./number-tasks-collection.component.html",
    styleUrls: ["./number-tasks-collection.component.scss"],
    standalone: false
})
export class PlanungApiOverviewNumberTaksCollectionComponent
    implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _renderer = inject(Renderer2);
    private _dts = inject(CstDateTimeService);
    isSaasInstance = inject(BFF_IsSaasInstance);

    @Input() overview: PlanungApiOverviewComponent;
    @Input() items: NumberTaskCollection[];

    @ViewChild(CdkVirtualScrollViewport)
    viewPort: CdkVirtualScrollViewport;

    private _subscription: Subscription;

    private isFirstScroll = true;
    private isAutoScrolling = false;
    private uiStatePrevGrouping = undefined;
    private uiStateHideTasksOverview = undefined;

    public itemType = ItemType;
    public collectionType = NumberTaskCollectionType;
    public resourceType = ResourceType;

    public flattenedList: any[] = null;

    public get itemSize() {
        return this.overview.uiState.CalendarGroupingActive ? 29.5 : 50;
    }

    public ngOnInit(): void {
        this._subscription = new Subscription();
        this._subscription.add(
            this.overview.uiState.changed.subscribe((newState) => {
                if (
                    newState.CalendarGroupingActive !== this.uiStatePrevGrouping ||
                    newState.HideTasksForOverview !== this.uiStateHideTasksOverview
                ) {
                    const hideChanged =
                        newState.HideTasksForOverview !== this.uiStateHideTasksOverview;
                    this.uiStatePrevGrouping = newState.CalendarGroupingActive;
                    this.uiStateHideTasksOverview = newState.HideTasksForOverview;
                    if (hideChanged) {
                        this.overview.uiState.ReferenceIndex = null;
                    }
                    this._ReloadItems();
                }
            })
        );
    }

    public ngAfterViewInit() {
        this._subscription.add(
            this.viewPort.scrolledIndexChange.subscribe((idx) => {
                if (this.isAutoScrolling || this.isFirstScroll) {
                    this.isFirstScroll = false;
                    return;
                }
                const item = this.flattenedList[idx];
                if (!item) {
                    return;
                }
                let refDate: Date = null;
                switch (item.contextInfo.type) {
                    case NumberTaskCollectionType.YearMonth:
                        refDate = new Date(
                            item.contextInfo.year,
                            item.contextInfo.number - 1,
                            1
                        );
                        break;
                    case NumberTaskCollectionType.CalendarWeek:
                        refDate = this._dts.GetDateFromCalendarWeek({
                            year: item.contextInfo.year,
                            week: item.contextInfo.number,
                        });
                        break;
                    case NumberTaskCollectionType.Day:
                        refDate = new Date(item.contextInfo.date);
                        break;
                    default:
                        return;
                }
                this.overview.uiState.ReferenceDate = refDate;
                this.overview.uiState.ReferenceIndex = idx;
                this.overview.uiState.ReferenceTask = null;
            })
        );
        if (this.overview.uiState.ReferenceTask) {
            setTimeout(() => {
                const idx = this._GetTaskIndex(this.overview.uiState.ReferenceTask);
                if (idx >= 0) {
                    this._ScrollToIndex(idx);
                }
            }, 1500);
        }
    }

    public ngOnDestroy(): void {
        if (this._subscription) {
            this._subscription.unsubscribe();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.items.currentValue !== changes.items.previousValue) {
            this._ReloadItems(false);
        }
    }

    public getDisplayedAbsencesForItem(item: any): IAbsenceItem[] {
        const __getAbsenceForDay = (resourceID: string, day: Date) => {
            const myAbsences = this.overview.absencesByResourceID
                ? this.overview.absencesByResourceID[resourceID]
                : undefined;

            if (myAbsences && myAbsences.length > 0) {
                return myAbsences.find((x) => this._dts.DateOnlyAreEqual(x.day, day));
            }
            return null;
        };
        const __getDisplayedAbsenceForDay = (resourceID: string, day: Date) => {
            const absenceDisplay = {
                label: day.toLocaleDateString(undefined, { weekday: "short" }),
                type: "free",
                tooltip: "",
            };
            const absenceForDay = __getAbsenceForDay(resourceID, day);
            if (absenceForDay) {
                absenceDisplay.type =
                    absenceForDay.level === UserAbsenceLevel.FullTime
                        ? "full"
                        : absenceForDay.level === UserAbsenceLevel.Partial
                            ? "partial"
                            : "free";
                absenceDisplay.tooltip = day.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) + "\r\n" + absenceForDay.reason;
            }
            return absenceDisplay;
        };

        const result = [];

        if (item.contextInfo.type === NumberTaskCollectionType.CalendarWeek) {
            const kw = item.contextInfo.number;
            let currDay = this._dts.GetDateFromCalendarWeek(
                { week: kw, year: item.contextInfo.year },
                DayOfWeek.Monday
            );
            for (let i = 0; i < 7; ++i) {
                const absenceDisplay = __getDisplayedAbsenceForDay(item.id, currDay);
                result.push(absenceDisplay);
                currDay = addDays(currDay, 1);
            }
        } else if (item.contextInfo.type === NumberTaskCollectionType.Day) {
            const absenceDisplay = __getDisplayedAbsenceForDay(item.id, item.contextInfo.date);
            result.push(absenceDisplay);
        }
        return result;
    }

    public GetItemSizeStyle() {
        const size = `${this.itemSize}px`;
        const lineHeight = `${this.itemSize}px`;
        return { height: size, lineHeight, overflow: "hidden" };
    }

    public GetSelectedStyle(task: TaskDto) {
        if (!this.overview.selection.IsItemSelected(task)) {
            return {};
        }
        return {
            border: "1px solid #009036",
            background: "#00903622",
            borderRadius: "10px",
        };
    }

    public OnDragStart(event: DragEvent, { task }) {
        this.overview.selection.HandleItem(task, event);
        this.overview.OnTasksDragStart(event, this.overview.selection.Items);
    }

    public OnDragOver(event: DragEvent, { contextInfo }) {
        event.preventDefault();
        if (
            !event.dataTransfer.types.includes(this.overview.DragDropKey_tasks) ||
            !contextInfo
        ) {
            event.dataTransfer.dropEffect = "none";
            return;
        }
        event.dataTransfer.dropEffect = "move";
        this._renderer.setStyle(
            event.currentTarget,
            "box-shadow",
            "inset 0 0 5px green"
        );
        this._renderer.setStyle(event.currentTarget, "border-radius", "10px");
    }
    public OnDragLeave(event: DragEvent, { _contextInfo }) {
        event.preventDefault();
        this._renderer.setStyle(event.currentTarget, "box-shadow", null);
    }

    public OnDrop(event: DragEvent, { contextInfo }) {
        event.preventDefault();
        if (
            !event.dataTransfer.types.includes(this.overview.DragDropKey_tasks) ||
            !contextInfo
        ) {
            return;
        }
        this._renderer.setStyle(event.currentTarget, "box-shadow", null);

        const yearPeriod = new YearPeriod({
            year: contextInfo.year,
            month: contextInfo.number,
        });
        const calendarWeek = new CalendarWeek({
            year: contextInfo.year,
            week: contextInfo.number,
        });
        const date = new Date(contextInfo.date);
        const newRes = contextInfo.resourceID;

        const tasks = this.overview.GetTasksFromDragEvent(event);
        if (!tasks || tasks.length === 0) {
            return;
        }
        switch (contextInfo.type) {
            case NumberTaskCollectionType.YearMonth:
                this.overview.SetTaskPeriodOfAction(tasks, yearPeriod, true, newRes);
                break;
            case NumberTaskCollectionType.CalendarWeek:
                this.overview.SetTaskPlannedWeek(tasks, calendarWeek, true, newRes);
                break;
            case NumberTaskCollectionType.Day:
                this.overview.SetTaskTodoDate(tasks, date, true, newRes);
                break;
        }
    }

    public OnSelectAll({ tasks }, ev: MouseEvent) {
        if (!tasks || this.overview.uiState.HideTasksForOverview) {
            return;
        }
        const allSelected = tasks.every((x: TaskDto) =>
            this.overview.selection.IsItemSelected(x)
        );
        if (!ev.ctrlKey) {
            this.overview.selection.Reset();
        }
        if (allSelected) {
            for (const task of tasks) {
                this.overview.selection.DeselectItem(task);
            }
        } else {
            for (const task of tasks) {
                this.overview.selection.SelectItem(task);
            }
        }
    }

    private _ReloadItems(scrollTaskID = true) {
        const local = [];
        for (const collection of this.items) {
            const dropDataMonthCW = {
                type: collection.Type,
                year: collection.Year,
                date: collection.Date,
                number: collection.Number,
                resourceID: undefined,
            };

            local.push({
                itemType: ItemType.MonthCW,
                collectionType: collection.Type,
                year: collection.Year,
                number: collection.Number,
                date: collection.Date,
                display: collection.Display,
                subDisplay: collection.SubDisplay,
                contextInfo: dropDataMonthCW,
                tasks: collection.Tasks,
            });

            if (this.overview.uiState.CalendarGroupingActive) {
                const resources =
                    this.overview.filter.Resources &&
                        this.overview.filter.Resources.length > 0 &&
                        this.overview.resources.length > 0
                        ? this.overview.filter.Resources.map((x) =>
                            this.overview.resources.find((o) => o.id === x)
                        )
                        : this.overview.resources;
                const ordered = orderBy(resources || [], [
                    { field: "type", dir: "asc" },
                    { field: "name", dir: "asc" },
                ]);
                for (const res of ordered) {
                    const resId = res.id === "null" ? null : res.id;
                    const resTasks = collection.Tasks.filter(
                        (t) =>
                            (t.claimResourceID && t.claimResourceID === resId) ||
                            (!t.claimResourceID && t.assignedToResourceID === resId) ||
                            (!t.claimResourceID && !t.assignedToResourceID && !resId)
                    );
                    const dropDataRes = {
                        type: collection.Type,
                        year: collection.Year,
                        number: collection.Number,
                        date: collection.Date,
                        resourceID: resId,
                    };
                    local.push({
                        itemType: ItemType.Resource,
                        name: res.name,
                        resType: res.type,
                        id: resId,
                        contextInfo: dropDataRes,
                        tasks: resTasks,
                    });

                    if (!this.overview.uiState.HideTasksForOverview) {
                        for (const task of resTasks) {
                            local.push({
                                itemType: ItemType.TaskCard,
                                task,
                                displayResource: false,
                                contextInfo: dropDataRes,
                            });
                        }
                    }
                }
            } else {
                for (const task of collection.Tasks) {
                    local.push({
                        itemType: ItemType.TaskCard,
                        task,
                        displayResource: true,
                        contextInfo: dropDataMonthCW,
                    });
                }
            }
        }
        setTimeout(() => {
            this.flattenedList = local;
            this._changeDetectorRef.detectChanges();

            let idx = this.overview.uiState.ReferenceIndex;
            if (scrollTaskID && this.overview.uiState.ReferenceTask) {
                const taskIdx = this._GetTaskIndex(this.overview.uiState.ReferenceTask);
                if (taskIdx >= 0) {
                    idx = taskIdx;
                }
                this.overview.uiState.ReferenceIndex = taskIdx;
            }

            if (idx === null) {
                const refDate = new Date(this.overview.uiState.ReferenceDate);
                const refYear = refDate.getFullYear();
                const refMonth = refDate.getMonth() + 1;
                const refCW = this._dts.GetCalendarWeekFromDate(refDate).week;
                idx = this.flattenedList.findIndex((x) => {
                    if (x.itemType !== ItemType.MonthCW) {
                        return false;
                    }
                    if (x.collectionType === NumberTaskCollectionType.YearMonth) {
                        return x.year === refYear && x.number === refMonth;
                    } else if (
                        x.collectionType === NumberTaskCollectionType.CalendarWeek
                    ) {
                        return x.year === refYear && x.number === refCW;
                    } else if (
                        x.collectionType === NumberTaskCollectionType.Day &&
                        x.date
                    ) {
                        return (
                            x.date.getFullYear() === refDate.getFullYear() &&
                            x.date.getMonth() === refDate.getMonth() &&
                            x.date.getDate() === refDate.getDate()
                        );
                    }
                    return false;
                });
            }
            this._ScrollToIndex(idx);
        }, 250);
    }

    private _GetTaskIndex(taskID: string) {
        return this.flattenedList.findIndex(
            (x) => x.itemType === ItemType.TaskCard && x.task && x.task.id === taskID
        );
    }

    private _ScrollToIndex(idx: number) {
        if (idx < 0) {
            return;
        }
        this.isAutoScrolling = true;
        this.viewPort.scrollToIndex(idx, "auto");
        setTimeout((__) => (this.isAutoScrolling = false), 250);
    }
}
